<script>
import ProductHelper from "@/utils/productHelper";
import ItemCard from "./ItemCard";
import KitCard from "./KitCard";
import ConfigSheet from "./ConfigSheet";
import { loadStoredConfig, saveStoredConfig } from "./storedConfig";
import {
  ITEM_CARD_CONFIG_KEY,
  INFO_FIELD_COUNT,
  PART_INFO_FIELD_COUNT,
  PART_COMMENT_FIELD_COUNT,
  DEFAULT_ITEM_CARD_CONFIG,
} from "./itemCardConfig";

export default {
  components: { ItemCard, KitCard, ConfigSheet },
  props: {
    products: { type: Array, default: () => [] },
    currentStep: { type: Object, default: null },
    steps: { type: Array, default: () => [] },
    availablePackagings: { type: Array, default: () => [] },
    packingFilter: { type: Object, default: null },
    nbAlerts: { type: Number, default: 0 },
    alertMode: { type: Boolean, default: false },
    showFilesButton: { type: Boolean, default: false },
    canUndoComplete: { type: Boolean, default: false },
  },
  data() {
    return {
      subTabIndex: 0,
      showFilters: false,
      showProducts: false,
      showDetached: false,
      showDamaged: false,
      showBO: false,
      showScanned: true,
      showNotScanned: true,
      showCompleted: true,
      inlineDetailProductId: null,
      dmgModeDismissSecs: 5,
      dmgModeDismissCountDown: 0,
      itemCardConfig: {
        collapseByDefault: DEFAULT_ITEM_CARD_CONFIG.collapseByDefault,
        visibleFields: { ...DEFAULT_ITEM_CARD_CONFIG.visibleFields },
      },
    };
  },
  created() {
    this.itemCardConfig = loadStoredConfig(
      ITEM_CARD_CONFIG_KEY,
      DEFAULT_ITEM_CARD_CONFIG,
    );
  },
  watch: {
    itemCardConfig: {
      deep: true,
      handler(val) {
        saveStoredConfig(ITEM_CARD_CONFIG_KEY, val);
      },
    },
    alertMode: {
      immediate: true,
      handler(val) {
        if (val) {
          this.dmgModeDismissCountDown = this.dmgModeDismissSecs;
        } else {
          this.dmgModeDismissCountDown = 0;
        }
      },
    },
  },
  computed: {
    filteredProducts() {
      var arr = this.products;
      if (this.showProducts) arr = arr.filter((x) => !x.isDetached);
      if (this.showDetached) arr = arr.filter((x) => x.isDetached);
      if (this.showDamaged) arr = arr.filter((x) => x.isDamaged);
      if (this.showBO) arr = arr.filter((x) => x.isBackOrder);
      arr = arr.filter((x) => {
        const scanned = ProductHelper.isProductStepCompleted(
          x,
          this.currentStep,
        );
        return scanned ? this.showScanned : this.showNotScanned;
      });
      return arr;
    },
    wantsScannedOnly() {
      return this.showScanned && !this.showNotScanned;
    },
    visibleProducts() {
      const allowCompleted = this.showCompleted || this.wantsScannedOnly;
      return this.filteredProducts.filter((p) =>
        ProductHelper.isProductVisible(p, this.currentStep, allowCompleted),
      );
    },
    kits() {
      const hash = {};
      const noKitKey = "__nokit__";
      for (const p of this.filteredProducts) {
        const key = p.kitName || noKitKey;
        if (!hash[key]) {
          hash[key] = {
            name: key === noKitKey ? this.$t("general.nokit") : key,
            products: [],
          };
        }
        hash[key].products.push(p);
      }
      return Object.values(hash);
    },
    selectedStep: {
      get() {
        return this.currentStep;
      },
      set(val) {
        if (val) this.$emit("select-step", val);
      },
    },
    productConfigFields() {
      return [
        { key: "workOrderName", label: this.$t("general.workorder") },
        { key: "packing", label: this.$t("general.packinglist") },
        { key: "room", label: this.$t("general.room") },
        { key: "kit", label: this.$t("general.kit") },
        { key: "dimensions", label: this.$t("general.dimensions") },
        { key: "comment", label: this.$t("general.comment") },
        { key: "barcode", label: this.$t("general.barcode") },
        { key: "rfidBarcode", label: this.$t("general.rfidbarcode") },
        { key: "kitBarcode", label: this.$t("general.kitbarcode") },
        { key: "weight", label: this.$t("general.weight") },
        { key: "comment2", label: `${this.$t("general.comment")} 2` },
        { key: "comment3", label: `${this.$t("general.comment")} 3` },
        ...Array.from({ length: INFO_FIELD_COUNT }, (_, i) => ({
          key: "info" + (i + 1),
          label: `${this.$t("general.info")} ${i + 1}`,
        })),
      ];
    },
    partConfigFields() {
      return [
        { key: "partDimensions", label: this.$t("general.dimensions") },
        { key: "partMaterial", label: this.$t("general.material") },
        ...Array.from({ length: PART_INFO_FIELD_COUNT }, (_, i) => ({
          key: "partInfo" + (i + 1),
          label: `${this.$t("general.info")} ${i + 1}`,
        })),
        ...Array.from({ length: PART_COMMENT_FIELD_COUNT }, (_, i) => ({
          key: "partComment" + (i + 1),
          label: `${this.$t("general.comment")} ${i + 1}`,
        })),
      ];
    },
    configSections() {
      return [
        {
          title: this.$t("general.default"),
          fields: [
            {
              key: "collapseByDefault",
              prop: "collapseByDefault",
              label: this.$t("general.collapsebydefault"),
            },
          ],
        },
        {
          title: this.$t("general.visiblefields"),
          fields: this.productConfigFields,
        },
        {
          title: this.$t("general.parts"),
          fields: this.partConfigFields,
        },
      ];
    },
  },
  methods: {
    dmgModeCountDownChanged(value) {
      this.dmgModeDismissCountDown = value;
    },
    selectPacking(option) {
      this.$emit("select-packing", option);
    },
    toggleAlertMode() {
      this.$emit("toggle-alert-mode");
    },
    toggleInlineDetail(productId) {
      this.inlineDetailProductId =
        this.inlineDetailProductId === productId ? null : productId;
    },
    onCardClick(product) {
      if (!this.alertMode) return;
      if (
        product.isDamaged &&
        product.parts &&
        product.parts.filter((x) => !x.isDamaged).length == 0
      ) {
        return;
      }
      this.$emit("open-alert", product);
    },
    onKitChildToggleInlineDetail(product) {
      this.toggleInlineDetail(product.idPublic);
    },
  },
};
</script>
<template>
  <div class="items">
    <div class="items-body">
      <b-tabs
        class="items-tabs"
        v-model="subTabIndex"
        nav-class="sub-tabs"
        active-nav-item-class="sub-tab-active"
        content-class="sub-content"
        no-fade
        justified
      >
        <template #tabs-end>
          <div class="d-flex fler-column border-0 m-1" style="gap: 0.3rem;">
            <button
              v-if="showFilesButton"
              class="icon-btn d-md-flex"
              :title="$t('general.files')"
              @click="$emit('open-files')"
            >
              <i class="mdi mdi-file-document-multiple-outline"></i>
            </button>
            <button
              class="icon-btn icon-btn-warn d-md-flex"
              :class="{ active: alertMode }"
              @click="toggleAlertMode"
              :title="$t('general.damaged')"
            >
              <i class="mdi mdi-alert-outline"></i>
              <span class="icon-badge" v-if="nbAlerts > 0">{{ nbAlerts }}</span>
            </button>
            <button
              class="icon-btn"
              :class="{ active: showFilters }"
              @click="showFilters = !showFilters"
              :title="$t('general.filters')"
            >
              <i class="mdi mdi-tune"></i>
            </button>
            <ConfigSheet v-model="itemCardConfig" :sections="configSections" />
          </div>
        </template>
        <div class="fpanel" :class="{ open: showFilters }">
          <div class="fpanel-inner">
            <div>
              <div class="fpanel-lbl">{{ $t("general.types") }}</div>
              <div class="fpanel-chips">
                <button
                  class="chip"
                  :class="{ 'on-dark': showProducts }"
                  @click="
                    showProducts = !showProducts;
                    if (showProducts) showDetached = false;
                  "
                >
                  <i class="mdi mdi-cube-outline"></i>
                  {{ $t("general.products") }}
                </button>
                <button
                  class="chip"
                  :class="{ 'on-dark': showDetached }"
                  @click="
                    showDetached = !showDetached;
                    if (showDetached) showProducts = false;
                  "
                >
                  <i class="mdi mdi-puzzle-outline"></i>
                  {{ $t("general.accessories") }}
                </button>
              </div>
            </div>
            <div>
              <div class="fpanel-lbl">{{ $t("general.status") }}</div>
              <div class="fpanel-chips">
                <button
                  class="chip"
                  :class="{ 'on-warn': showDamaged }"
                  @click="
                    showDamaged = !showDamaged;
                    if (showDamaged) showBO = false;
                  "
                >
                  <i class="mdi mdi-alert-outline"></i>
                  {{ $t("general.damaged") }}
                </button>
                <button
                  class="chip"
                  :class="{ 'on-danger': showBO }"
                  @click="
                    showBO = !showBO;
                    if (showBO) showDamaged = false;
                  "
                >
                  <i class="mdi mdi-minus-circle-outline"></i>
                  {{ $t("general.bo") }}
                </button>
              </div>
            </div>
            <div>
              <div class="fpanel-lbl">{{ $t("general.scanstatus") }}</div>
              <div class="fpanel-chips">
                <button
                  class="chip"
                  :class="{ 'on-success': showScanned }"
                  @click="showScanned = !showScanned"
                >
                  <i class="mdi mdi-qrcode-scan"></i>
                  {{ $t("general.scanned") }}
                </button>
                <button
                  class="chip"
                  :class="{ 'on-dark': showNotScanned }"
                  @click="showNotScanned = !showNotScanned"
                >
                  <i class="mdi mdi-qrcode-remove"></i>
                  {{ $t("general.notscanned") }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <b-alert 
          v-model="dmgModeDismissCountDown"
          fade
          variant="warning"
          class="mode-banner p-0 m-0"
          dismissible
          @dismissed="dmgModeDismissCountDown=0"
          @dismiss-count-down="dmgModeCountDownChanged"
        >
        <div class="content">
          <i class="mdi mdi-alert-outline mode-banner-ico mdi-24px"></i>
          <div class="mode-banner-txt">
            <div class="font-weight-bold">{{ $t("general.damagescanmode") }}</div>
            <div class="mode-banner-hint">{{ $t("general.damagescanhint") }}</div>
          </div>
        </div>
          <b-progress
            variant="warning"
            :max="dmgModeDismissSecs-1"
            :value="dmgModeDismissCountDown-1"
            height="4px"
          ></b-progress>
        </b-alert>
        <b-tab :title="$t('general.products').toUpperCase()" title-link-class="d-flex align-items-center justify-content-center h-100">
          <div class="ilist">
            <ItemCard
              v-for="product in visibleProducts"
              :key="product.idPublic"
              :product="product"
              :current-step="currentStep"
              :alert-mode="alertMode"
              :inline-detail-open="inlineDetailProductId === product.idPublic"
              :config="itemCardConfig"
              :can-undo="canUndoComplete"
              @uncheck="$emit('undo-completed', [product])"
              @card-click="onCardClick(product)"
              @check="$emit('confirm-completed', [product])"
              @toggle-inline-detail="toggleInlineDetail(product.idPublic)"
              @open-alert="$emit('open-alert', product)"
            />

            <div v-if="visibleProducts.length === 0" class="empty">
              {{ $t("general.notcompletedonly") }}…
            </div>
          </div>
        </b-tab>
        <b-tab :title="$t('general.kit').toUpperCase()" title-link-class="d-flex align-items-center justify-content-center h-100">
          <div class="ilist">
            <KitCard
              v-for="kit in kits"
              :key="kit.name"
              :kit="kit"
              :current-step="currentStep"
              :alert-mode="alertMode"
              :show-completed="showCompleted || wantsScannedOnly"
              :inline-detail-product-id="inlineDetailProductId"
              :config="itemCardConfig"
              :can-undo="canUndoComplete"
              @kit-uncheck="(prods) => $emit('undo-completed', prods)"
              @kit-check="(prods) => $emit('confirm-completed', prods)"
              @child-check="(p) => $emit('confirm-completed', [p])"
              @child-uncheck="(p) => $emit('undo-completed', [p])"
              @child-open-alert="(p) => $emit('open-alert', p)"
              @child-toggle-inline-detail="onKitChildToggleInlineDetail"
              @child-card-click="onCardClick"
            />

            <div v-if="kits.length === 0" class="empty">
              {{ $t("general.notcompletedonly") }}…
            </div>
          </div>
        </b-tab>
      </b-tabs>

    </div>
  </div>
</template>
<style scoped>
.items {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
}
.items-sticky {
  background: white;
  border-bottom: 1px solid #e0e5ea;
  position: relative;
}
.topbar {
  background: white;
  padding: 8px 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}
.status-select {
  flex: 1;
  min-width: 0;
}
.status-select ::v-deep .multiselect__tags {
  background: #f4f6f8;
  border: 1px solid #e0e5ea;
  border-radius: 9px;
  min-height: 34px;
  padding: 6px 32px 0 10px;
}
.status-select ::v-deep .multiselect__single {
  background: transparent;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1c2e3e;
}
.sdot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34c38f;
  flex-shrink: 0;
  display: inline-block;
}
.slbl {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.shint {
  font-size: 10px;
  color: #7a8a99;
  white-space: nowrap;
  font-weight: 500;
}
.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid #e0e5ea;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a8a99;
  font-size: 17px;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
}
.icon-btn.active {
  background: #1c2e3e;
  color: white;
  border-color: #1c2e3e;
}
.icon-btn-warn {
  background: #fff8e1;
  color: #f1b44c;
  border: 1px solid #f9d77e;
}
.icon-btn-warn.active {
  background: #f1b44c;
  color: white;
  border-color: #f1b44c;
}
.icon-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #f1b44c;
  color: white;
  font-size: 9px;
  font-weight: 700;
  border-radius: 7px;
  padding: 1px 4px;
  border: 2px solid white;
}
.mode-banner .content {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 11px;
  margin: 0;
  line-height: 1;
  padding-right: 30px;
}
::v-deep .progress-bar {
  transition: width 1s linear;
}
.fpanel {
  background: white;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.25s ease;
}
.fpanel.open {
  max-height: 500px;
}
.fpanel-inner {
  padding: 11px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fpanel-row-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.fpanel-strong {
  font-size: 13px;
  font-weight: 600;
  color: #1c2e3e;
}
.fpanel-lbl {
  font-size: 10px;
  font-weight: 600;
  color: #7a8a99;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}
.fpanel-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.chip {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #e0e5ea;
  background: white;
  color: #7a8a99;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.chip i {
  font-size: 12px;
}
.chip.on-dark {
  background: #1c2e3e;
  color: white;
  border-color: #1c2e3e;
}
.chip.on-warn {
  background: #f1b44c;
  color: white;
  border-color: #f1b44c;
}
.chip.on-danger {
  background: #e74c3c;
  color: white;
  border-color: #e74c3c;
}
.chip.on-success {
  background: #34c38f;
  color: white;
  border-color: #34c38f;
}
.items ::v-deep .sub-tabs {
  background: white;
  border-bottom: 1px solid #e0e5ea;
  border-top: none;
  margin: 0;
}
.items ::v-deep .sub-tabs .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #7a8a99;
  background: none;
}
.items ::v-deep .sub-tabs .sub-tab-active {
  color: #1c2e3e;
  border-bottom-color: #f1b44c;
  background: none;
}
.items ::v-deep .sub-content {
  background: transparent;
}
.items-body {
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.items-tabs{
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.tab-content{
  min-height: 0;
}
.ilist {
  padding: 9px 11px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.empty {
  padding: 24px 11px;
  text-align: center;
  color: #7a8a99;
  font-size: 12px;
}
</style>
